import { createFocusTrap } from 'focus-trap';
import type { RefObject } from 'react';
import { useEffect, useRef } from 'react';

interface UseFocusTrapOptions {
  active: boolean;
  containerRef: RefObject<HTMLElement | null>;
  returnFocusRef?: RefObject<HTMLElement | null>;
  resolveReturnFocus?: () => HTMLElement | false | undefined;
  onDeactivate?: () => void;
  clickOutsideDeactivates?: boolean;
  escapeDeactivates?: boolean;
  allowOutsideClick?: boolean;
}

export function useFocusTrap({
  active,
  containerRef,
  returnFocusRef,
  resolveReturnFocus,
  onDeactivate,
  clickOutsideDeactivates = false,
  escapeDeactivates = false,
  allowOutsideClick = false,
}: UseFocusTrapOptions) {
  const onDeactivateRef = useRef(onDeactivate);
  const resolveReturnFocusRef = useRef(resolveReturnFocus);

  onDeactivateRef.current = onDeactivate;
  resolveReturnFocusRef.current = resolveReturnFocus;

  useEffect(() => {
    const container = containerRef.current;

    if (!active || !container) {
      return;
    }

    const trap = createFocusTrap(container, {
      allowOutsideClick,
      clickOutsideDeactivates,
      escapeDeactivates,
      fallbackFocus: container,
      onDeactivate: () => onDeactivateRef.current?.(),
      setReturnFocus: () =>
        resolveReturnFocusRef.current?.() ?? returnFocusRef?.current ?? false,
      tabbableOptions: {
        displayCheck: 'none',
      },
    });

    trap.activate();

    return () => {
      trap.deactivate();
    };
  }, [
    active,
    allowOutsideClick,
    clickOutsideDeactivates,
    containerRef,
    escapeDeactivates,
    returnFocusRef,
  ]);
}
